import type { TableSearchParameters } from '@/ui/Table/tableProperties';
import type { ColumnsAvailableProps } from '@/ui/Table/useColumnsFilter';
import useColumnsFilter from '@/ui/Table/useColumnsFilter';
import TableColumnStorage from '@/util/localStorage/TableColumnStorage';
import type { FileAiPromptFragmentFragment } from '@/graphql/generated/graphql';
import { protolusColumnsAvailable, protolusColumnsOrder } from '~/ui/Table/protulus/protolusColumns';

type Columns = keyof FileAiPromptFragmentFragment | 'onSelect' | 'actions';

const columnsOrder: Columns[] = [
  'id',
  'type',
  'version',
  'status',
  'prompt',
  'schema',
  'onSelect',
  'actions',
  ...protolusColumnsOrder,
];

const storage = new TableColumnStorage<Columns[]>('file-ai-prompts', [
  'id',
  'type',
  'version',
  'status',
  'prompt',
  'schema',
]);

const columnsAvailablesBase: ColumnsAvailableProps<Columns> = {
  id: {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 72,
    sorter: true,
  },
  type: {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 100,
    sorter: true,
  },
  version: {
    title: 'Version',
    dataIndex: 'version',
    key: 'version',
    width: 100,
    sorter: true,
  },
  status: {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    sorter: true,
  },
  prompt: {
    title: 'Prompt',
    dataIndex: 'prompt',
    key: 'prompt',
    ellipsis: true,
    sorter: true,
  },
  schema: {
    title: 'Schema',
    dataIndex: 'schema',
    key: 'schema',
    ellipsis: true,
    sorter: true,
  },
  actions: {
    title: 'Actions',
    key: 'actions',
    alwaysVisible: true,
    hide: false,
    width: 88,
    fixed: 'right',
  },
  ...protolusColumnsAvailable,
};

interface FileAiPromptsColumnsArgs {
  orderBy: TableSearchParameters['orderBy'];
  showSelectColumn?: boolean;
}

const useFileAiPromptsColumns = ({ orderBy, showSelectColumn }: FileAiPromptsColumnsArgs) => {
  const columnsAvailables: ColumnsAvailableProps<Columns> = {
    ...columnsAvailablesBase,
    onSelect: {
      title: 'Select',
      key: 'onSelect',
      alwaysVisible: showSelectColumn,
      hide: !showSelectColumn,
      width: 88,
      fixed: 'right',
    },
  };

  return useColumnsFilter({
    orderBy,
    storage,
    columnsOrder,
    columnsAvailables,
  });
};

export default useFileAiPromptsColumns;
